// dependencies
import React, { PropsWithChildren } from "react";

import Typography from "../Text";
import Tooltip from "../Tooltip";
import css from "./Badge.module.scss";

interface BaseProps {
	/**
	 * Number to be displayed in badge
	 */
	count: number;
	/**
	 * Optional title to be displayed in tooltip
	 */
	title?: string;
	/**
	 * If true, badge will display 0 if count is 0
	 */
	showZero?: boolean;
	/**
	 * If specified, will display overflowCount+ if count exceeds overflowCount
	 */
	overflowCount?: number;
	/**
	 * Optional className to be added to badge for custom styling
	 * @default
	 */
	className?: string;
	/**
	 * Optional status to be added to badge for custom styling
	 * @default null
	 */
	status?: "success" | "warning" | "error" | "info" | "default";
}

/**
 * Wraps any component and displays a badge with a count. Typically used to
 * display the number of activated filters. Also useful for displaying
 * notifications, messages, etc.
 */
const Badge = (props: PropsWithChildren<BaseProps>) => {
	const { className, children, count, overflowCount, showZero, title, status } = props;

	// if overfllowCount is specified and count exceeds that number, display the overflolwCount, otherwise display the count
	const countDisplay = overflowCount !== undefined && count > overflowCount ? `${overflowCount}+` : count;

	// check if singleDigit or not to determine the necessary padding space
	const isSingleDigit = countDisplay.toString().length === 1;

	const badgeHtml = (
		<div
			className={` ${css.countIndicator} ${isSingleDigit ? css.singleDigit : ""} ${status ? css[`status-${status}`] : css[`status-default`]} ${className || ""} `}
		>
			<Typography size="paragraph-3" weight="regular">
				{countDisplay}
			</Typography>
		</div>
	);

	return (
		<div className={className}>
			{children}
			{showZero !== true && count === 0 ? null : (
				<>
					{title ? (
						<div className={css.tooltipWrapper}>
							<Tooltip message={title}>{badgeHtml}</Tooltip>
						</div>
					) : (
						badgeHtml
					)}
				</>
			)}
		</div>
	);
};

export default Badge;
